import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { V2, pickLang } from "../copy";
import { isLanding, route } from "../paths";
import { Arrow } from "./Primitives";

/**
 * Floating pill navigation. Transparent over the hero, frosted ink once the
 * page scrolls. Mobile opens a full-screen ink menu with serif links.
 * Section links scroll on the landing and navigate back to it (with the
 * hash) from the sub-pages.
 */
const NavV2: React.FC = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].nav;
  const { pathname } = useLocation();
  const onLanding = isLanding(pathname, lang);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close the mobile menu on route change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const links: [string, string][] = [
    ["v2-reel", c.reel],
    ["v2-services", c.services],
    ["v2-process", c.process],
    ["v2-pricing", c.pricing],
    ["v2-faq", c.faq],
  ];

  const other = lang === "fr" ? "en" : "fr";

  const SectionLink: React.FC<{
    id: string;
    className: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
  }> = ({ id, className, style, children }) =>
    onLanding ? (
      <a href={`#${id}`} className={className} style={style} onClick={() => setOpen(false)}>
        {children}
      </a>
    ) : (
      <Link to={`${route(lang)}#${id}`} className={className} style={style} onClick={() => setOpen(false)}>
        {children}
      </Link>
    );

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        <div className="mx-auto mt-3 max-w-[1400px] px-3 sm:mt-4 sm:px-6">
          <div
            className={`flex items-center justify-between rounded-full border px-3 py-2 pl-4 transition-all duration-500 sm:px-4 ${
              scrolled || open
                ? "border-bone/10 bg-ink/70 backdrop-blur-xl shadow-[0_20px_60px_-30px_rgba(0,0,0,0.8)]"
                : "border-transparent bg-transparent"
            }`}
          >
            <Link
              to={route(lang)}
              aria-label="Lave Tes Baskets"
              className="flex items-center gap-3"
              onClick={(e) => {
                if (onLanding) {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
                setOpen(false);
              }}
            >
              <img
                src="/lavetesbasket_logow.png"
                alt="Lave Tes Baskets"
                className="h-9 w-auto object-contain sm:h-10"
              />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
              {links.map(([id, label]) => (
                <SectionLink
                  key={id}
                  id={id}
                  className="rounded-full px-3.5 py-2 text-[13px] font-medium text-bone/75 transition-colors hover:bg-bone/10 hover:text-bone"
                >
                  {label}
                </SectionLink>
              ))}
              <Link
                to={route(lang, "entreprises")}
                className="rounded-full px-3.5 py-2 text-[13px] font-medium text-bone/75 transition-colors hover:bg-bone/10 hover:text-bone"
              >
                {c.b2b}
              </Link>
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => changeLanguage(other)}
                className="hidden rounded-full px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-bone/70 transition-colors hover:text-bone sm:block"
                aria-label={`Switch to ${other === "fr" ? "French" : "English"}`}
              >
                {`${lang} `}<span className="opacity-40">/</span>{` ${other}`}
              </button>
              <Link
                to={`/${lang}/login`}
                className="hidden rounded-full px-3.5 py-2 text-[13px] font-medium text-bone/75 transition-colors hover:text-bone lg:block"
              >
                {c.login}
              </Link>
              <Link
                to={`/${lang}/signup`}
                className="v2-btn v2-btn-solid hidden !px-4 !py-2.5 text-[13px] sm:inline-flex"
              >
                {c.cta}
                <Arrow />
              </Link>
              <button
                onClick={() => setOpen((o) => !o)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-bone/15 text-bone lg:hidden"
                aria-label={open ? c.close : c.menu}
                aria-expanded={open}
              >
                <span className="relative block h-3 w-4">
                  <span
                    className={`absolute left-0 top-0 h-px w-full bg-current transition-transform duration-300 ${
                      open ? "translate-y-[6px] rotate-45" : ""
                    }`}
                  />
                  <span
                    className={`absolute left-0 top-[6px] h-px w-full bg-current transition-opacity duration-300 ${
                      open ? "opacity-0" : ""
                    }`}
                  />
                  <span
                    className={`absolute left-0 top-3 h-px w-full bg-current transition-transform duration-300 ${
                      open ? "-translate-y-[6px] -rotate-45" : ""
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile full-screen menu */}
      <div
        className={`fixed inset-0 z-40 flex flex-col bg-ink px-6 pb-8 pt-28 text-bone transition-opacity duration-500 lg:hidden ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden={!open}
      >
        <nav className="flex flex-col" aria-label="Mobile">
          {[...links, ["__b2b", c.b2b] as [string, string]].map(([id, label], i) => {
            const cls =
              "flex items-baseline justify-between border-b border-bone/10 py-4 font-display text-4xl leading-none transition-all duration-500";
            const style = {
              transitionDelay: open ? `${80 + i * 50}ms` : "0ms",
              transform: open ? "translateY(0)" : "translateY(14px)",
              opacity: open ? 1 : 0,
            };
            const inner = (
              <>
                <span>{label}</span>
                <span className="font-mono text-[11px] tracking-[0.2em] text-bone/40">
                  {`0${i + 1}`}
                </span>
              </>
            );
            return id === "__b2b" ? (
              <Link key={id} to={route(lang, "entreprises")} className={cls} style={style} onClick={() => setOpen(false)}>
                {inner}
              </Link>
            ) : (
              <SectionLink key={id} id={id} className={cls} style={style}>
                {inner}
              </SectionLink>
            );
          })}
        </nav>
        <div className="mt-auto flex flex-col gap-3">
          <Link
            to={`/${lang}/signup`}
            className="v2-btn v2-btn-solid justify-center"
            onClick={() => setOpen(false)}
          >
            {c.cta}
            <Arrow />
          </Link>
          <div className="flex items-center justify-between">
            <Link
              to={`/${lang}/login`}
              className="v2-link text-sm text-bone/80"
              onClick={() => setOpen(false)}
            >
              {c.login}
            </Link>
            <button
              onClick={() => changeLanguage(other)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-bone/70"
            >
              {`${lang} `}<span className="opacity-40">/</span>{` ${other}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavV2;
